/** Typed wrappers around the unified-history REST API.
 *
 * Matches the §1.6 Part 3a endpoint shape (every method passes the
 * ``X-Tenant-ID`` header via `api()` — caller doesn't touch it).
 */

import { api } from "./api";

export interface Conversation {
  id: string;
  tenant_id: string;
  channel: string;
  platform_conversation_id: string;
  customer_id: string;
  endpoint_id: string;
  title: string;
  status: "active" | "completed" | "error" | string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  sentiment_label: string;
  sentiment_score: number;
  summary_text: string;
  primary_recording_asset_id: string;
  /** §outcome-rows — 'order:<id>' / 'ticket:<id>' stamped when the
   * conversation produced a real outcome; '' otherwise. */
  outcome_ref?: string;
  /** Server-resolved chip for the ref: what actually happened. */
  outcome?: {
    kind: "order" | "ticket";
    ref: string;
    status?: string;
    amount?: number | string | null;
    currency?: string;
    issue_type?: string;
  };
}

export interface Customer {
  id: string;
  tenant_id: string;
  display_name: string;
  phone_e164: string;
  telegram_chat_id: string;
  whatsapp_wa_id: string;
  desktop_user_id: string;
  metadata_json: string;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  tenant_id: string;
  conversation_id: string;
  channel: string;
  role: "user" | "assistant" | "system" | "tool" | string;
  content_type: "text" | "audio" | "image" | "tool" | string;
  text_content: string;
  transcript_text: string;
  audio_asset_id: string;
  image_asset_id: string;
  tool_name: string;
  tool_call_id: string;
  event_type: string;
  happened_at: string;
  sequence_no: number;
  metadata_json: string;
}

export interface TranscriptSegment {
  id: string;
  tenant_id: string;
  conversation_id: string;
  message_id: string;
  role: string;
  source: string;
  channel: string;
  speaker_label: string;
  text: string;
  start_ms: number;
  end_ms: number;
  confidence: number;
  language_code: string;
  received_at: string;
  // §monitor-english (2026-08-12) — English mirror of a non-English turn,
  // translated once when the turn was recorded. `text` above ALWAYS remains
  // what the customer actually said; this is a reading aid beside it.
  /** English rendering; "" for English turns and for failed translations. */
  text_en?: string;
  /** "ok" | "unavailable" | "" (English turn / never attempted). */
  translation_status?: string;
  /** Display name of the spoken language ("Arabic"); "" when unknown. */
  language_label?: string;
}

export interface MediaAsset {
  id: string;
  tenant_id: string;
  conversation_id: string;
  message_id: string;
  kind: string;
  original_filename: string;
  mime_type: string;
  storage_path: string;
  file_size_bytes: number;
  duration_ms: number;
  created_at: string;
}

/**
 * §13.1.e — high-level category filter that expands server-side
 * to a multi-channel OR match. "customer" = end-user channels
 * (phone, whatsapp, telegram, etc.); "admin" = operator
 * playgrounds (test-voice, test-chat). Mutually exclusive with
 * an explicit `channel` filter — if both are supplied, the
 * backend respects `channel` and ignores `category`.
 */
export type ConversationCategory = "customer" | "admin";

export interface ConversationListFilters {
  channel?: string;
  category?: ConversationCategory;
  status?: string;
  sentiment?: string;
  customer_id?: string;
  q?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

function buildQuery(filters: ConversationListFilters): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v != null && v !== "") params.set(k, String(v));
  }
  const q = params.toString();
  return q ? `?${q}` : "";
}

export async function listConversations(filters: ConversationListFilters = {}) {
  return api<{ conversations: Conversation[]; count: number }>(
    `/api/conversations${buildQuery(filters)}`,
  );
}

// §v3.enquiries (2026-05-08) — conversations that did NOT result
// in an order or a support ticket. The dashboard /enquiries page
// uses this. Backend filters orders/tickets out; frontend just
// renders.
// §v3.enquiries-classifier — Grok-backed intent classification.
// "order_placed" / "support_issue" mean the conversation should
// arguably be in another section; "info_only" is the genuine
// asked-didn't-buy case; "abandoned" cut off too early to tell;
// "unclear" means the LLM couldn't decide (or hasn't run yet).
export type EnquiryIntent =
  | "order_placed"
  | "support_issue"
  | "info_only"
  | "abandoned"
  | "unclear";

export interface EnquiryClassification {
  intent: EnquiryIntent;
  confidence: "high" | "medium" | "low";
  reason: string;
}

export interface EnquiryRow {
  id: string;
  channel: string;
  status: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  sentiment_label: string;
  summary_text: string;
  msg_count: number;
  user_msg_count: number;
  /** "engaged" — ≥2 user messages; "abandoned" — 0/1 user msgs (silent drop). */
  engagement: "engaged" | "abandoned";
  customer: {
    id: string;
    display_name: string;
    phone_e164: string;
  };
  last_user_message: string;
  classification: EnquiryClassification;
}

export interface EnquiriesListFilters {
  channel?: string;
  engagement?: "engaged" | "abandoned" | "";
  intent?: EnquiryIntent | "";
  sentiment?: string;
  q?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  /** Backend Grok classifier mode. ``auto`` (default) classifies
   *  rows that have no cached label, bounded by ``max_classify``.
   *  ``force`` re-classifies every row. ``off`` returns whatever's
   *  cached (or "unclear" otherwise). */
  classify?: "auto" | "force" | "off";
  max_classify?: number;
}

export interface EnquiriesListResponse {
  enquiries: EnquiryRow[];
  count: number;
  /** §logged-enquiries-surfaced — the chat agent's "passed to the team"
   *  ledger (state.inquiries). Captured name/phone/topic that no page in
   *  the product ever displayed. */
  logged_enquiries?: Array<{
    id: number;
    conversation_id: string;
    customer_identifier: string;
    channel: string;
    topic: string;
    question_text: string;
    resolution: string;
    created_at: number;
  }>;
  kpis: {
    total: number;
    engaged: number;
    abandoned: number;
    by_channel: Record<string, number>;
    /** §v3.enquiries-classifier — count per intent label. */
    by_intent: Partial<Record<EnquiryIntent, number>>;
    /** Number of LLM classifications run on this request (0 if
     *  every row was already cached). Operator-facing only —
     *  surfaces as a small chip if non-zero. */
    classifications_run_now: number;
  };
}

export async function listEnquiries(
  filters: EnquiriesListFilters = {},
): Promise<EnquiriesListResponse> {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v != null && v !== "") q.set(k, String(v));
  }
  const qs = q.toString();
  return api<EnquiriesListResponse>(
    `/api/admin/enquiries${qs ? "?" + qs : ""}`,
  );
}

// §v3.cumulative-transcript-collapse (2026-05-23) — the xAI realtime voice
// model re-emits each user turn CUMULATIVELY (growing prefixes of the same
// utterance), and the backend persists every copy as its own messages /
// transcript_segments row (no turn_id to tie them together). That made saved
// call transcripts show one utterance as ~5 stacked rows. We collapse them at
// READ time so every transcript surface (Enquiries, Customer 360, Order detail)
// shows a single clean line — keeping the LONGEST (most complete) version of
// each cumulative group. Reuses the normalize + word-boundary prefix rule
// proven in the live view (LiveCalls.tsx). Display-only; the stored rows are
// untouched (a separate backend collapse handles data hygiene for summaries).
function collapseCumulative<T extends { role: string }>(
  items: T[],
  getText: (x: T) => string,
): T[] {
  const norm = (s: string) =>
    (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const out: T[] = [];
  for (const it of items) {
    const text = getText(it) || "";
    const nrm = norm(text);
    const prev = out[out.length - 1];
    if (prev && prev.role === it.role && nrm) {
      const prevText = getText(prev) || "";
      const pNorm = norm(prevText);
      // Same consecutive role + one is a (word-boundary) prefix of the other,
      // or an exact normalized match → cumulative re-emit of one turn.
      if (
        pNorm &&
        (nrm === pNorm ||
          nrm.startsWith(pNorm + " ") ||
          pNorm.startsWith(nrm + " "))
      ) {
        if (text.length > prevText.length) out[out.length - 1] = it; // keep longest
        continue;
      }
    }
    out.push(it);
  }
  return out;
}

/** §translations — detected language + English translation for every
 *  non-English message of a conversation (computed once, cached server-side). */
export interface MessageTranslation {
  lang: string;
  lang_name: string;
  translation: string;
}
export async function getConversationTranslations(id: string) {
  const data = await api<{ translations: Record<string, MessageTranslation> }>(
    `/api/conversations/${encodeURIComponent(id)}/translations`,
    { method: "POST" },
  );
  return data.translations || {};
}

export async function getConversation(id: string) {
  const data = await api<{
    conversation: Conversation;
    messages: Message[];
    transcript_segments: TranscriptSegment[];
    media_assets: MediaAsset[];
  }>(`/api/conversations/${encodeURIComponent(id)}`);
  return {
    ...data,
    messages: collapseCumulative(
      data.messages ?? [],
      (m) => m.text_content || m.transcript_text || "",
    ),
    transcript_segments: collapseCumulative(
      data.transcript_segments ?? [],
      (s) => s.text || "",
    ),
  };
}

/** §v3.conv-summarizer (2026-05-07) — manually trigger Grok-based
 *  AI summary regeneration for a single conversation. The dashboard
 *  modals offer this as a "Regenerate" button next to the AI summary
 *  card label. The endpoint always returns 200 with
 *  ``{summary: ""}`` on failure (no API key, no transcript, xAI
 *  outage), so the caller decides whether to render the result or
 *  show the previous summary. */
export async function regenerateConversationSummary(id: string) {
  return api<{ conversation_id: string; summary: string }>(
    `/api/conversations/${encodeURIComponent(id)}/regenerate-summary`,
    { method: "POST" },
  );
}

/**
 * §13.1-hotfix-v2 — authenticated-fetch → blob → object URL for
 * `<audio src>` playback in the Inbox.
 *
 * Prior iteration (`buildMediaStreamUrl`) tried to pass the JWT
 * as a `?secret=…` query param so the native `<audio>` element
 * could fetch without sending an Authorization header. That
 * worked against the legacy `ADMIN_SECRET` path but the server's
 * `_resolve_admin_context` checks JWTs ONLY on the Bearer header;
 * a JWT in the query param falls through to the legacy-secret /
 * operator-session compare, neither of which match a JWT, so the
 * request returns **403 "Invalid admin credentials"**. Putting
 * JWTs in URLs is also a general footgun (referer headers,
 * browser history, server access logs leak the bearer).
 *
 * This helper does the authenticated fetch via `apiFetchBlob`
 * (same tenant header, same Bearer JWT, same 401→refresh retry
 * the rest of the admin API uses) and returns a browser-local
 * `blob:` URL that `<audio>` can play without any auth. The
 * caller MUST call `URL.revokeObjectURL` when the component
 * unmounts or the asset changes, or the browser leaks the Blob
 * (small — 1–10 MB typical — but it adds up on long sessions).
 *
 * Trade-offs accepted:
 *   * No server-side HTTP Range — the full file is fetched before
 *     playback starts. For the Opus/WebM recordings Test Voice
 *     and SIP paths emit this is sub-1s even on ADSL. Seeks still
 *     work within the Blob.
 *   * No URL-leak of the token (big win over the previous
 *     `?secret=JWT` attempt).
 */
import { apiFetchBlob } from "./api";

export async function fetchMediaAsObjectUrl(
  assetId: string,
): Promise<string> {
  const blob = await apiFetchBlob(
    `/api/media/${encodeURIComponent(assetId)}`,
  );
  return URL.createObjectURL(blob);
}
